/* =========================================================================
   Play HUD (PLAN-design-round 2026-08-21 §A.1) — the reference's game header:

       ( ◀ )   [ Round 3 · American Catholics ]   ( ⋯ )
               💰 $4,828  ·  Q3/10  ·  🔥 5

   ◀ opens the leave sheet (ui/leaveRound.ts), the pill names the round, and
   the ⋯ slot HOSTS the persistent account avatar (#accountMenu is re-parented
   into #hudMenuSlot while the play screen is up — so the menu is reachable
   mid-round instead of vanishing, the "trapped" complaint). The stat strip
   absorbs the bank pill, so the top bankbar is hidden on play; `#bankVal`
   stays wired for the pause screens.

   This module is DOM setters + pure label helpers only. It deliberately does
   NOT import gameController (which calls into it) — the wiring of ◀ → sheet
   lives in gameController.initPlayHud(), and the per-screen sync rides on
   accountMenu.syncAccountMenu (the one hook called on every transition).
   ========================================================================= */
import { fmtCents } from '../engine';
import { icon } from './icons';

export const HUD_SCREENS: ReadonlySet<string> = new Set(['play', 'preroll']);

/** Markup for the top of the play screen. The live ids gameController writes
    (#catChip, #qProg) are kept; #hudRound / #hudBank / #hudStreak are new. */
export const playHudHTML = /* html */ `
<div class="play-hud" id="playHud">
  <div class="hud-row">
    <button class="icon-btn hud-btn" id="hudBackBtn" type="button" aria-label="Pause — leave this round?" aria-haspopup="dialog">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15.5 4.5 8 12l7.5 7.5" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>
    </button>
    <div class="hud-title" id="hudTitle"><span class="hud-round" id="hudRound">Round 1</span></div>
    <div class="hud-menu-slot" id="hudMenuSlot"></div>
  </div>
  <div class="hud-stats" role="status" aria-live="off">
    <span class="hud-stat hud-bank" id="hudBankPill"><span class="hud-ic" aria-hidden="true">${icon('coin', 14)}</span><span class="money" id="hudBank">$0</span><span id="hudFloatDelta" class="float-delta"></span></span>
    <span class="hud-sep" aria-hidden="true"></span>
    <span class="hud-stat qprog" id="qProg">Q1/10</span>
    <span class="hud-sep hud-streak-sep hidden" aria-hidden="true"></span>
    <span class="hud-stat hud-streak hidden" id="hudStreak" title="Daily streak"><span class="hud-ic" aria-hidden="true">${icon('flame', 14)}</span><span id="hudStreakN">0</span></span>
  </div>
</div>`;

/* ------------------------------- pure labels ------------------------------- */

/** "Round 3 · American Catholics" (idx is 0-based). PURE. */
export function hudRoundLabel(roundIndex: number, roundName: string): string {
  const name = (roundName || '').trim();
  return name ? `Round ${roundIndex + 1} · ${name}` : `Round ${roundIndex + 1}`;
}

/** "Q3/10" — compact progress cell (the old header said "Q3 / 10"). PURE. */
export function hudProgress(question: number, total: number): string {
  return `Q${question}/${total}`;
}

/** The streak cell's NUMBER text (the flame icon lives in the markup), or
    null when the cell should be hidden (guest / no streak / unknown). PURE. */
export function hudStreakLabel(streak: number | null | undefined, authed: boolean): string | null {
  if (!authed || streak == null || !Number.isFinite(streak) || streak <= 0) return null;
  return `${Math.floor(streak)}`;
}

/* ------------------------------- DOM setters ------------------------------- */

function el(id: string): HTMLElement | null {
  return typeof document === 'undefined' ? null : document.getElementById(id);
}

export function setHudRound(roundIndex: number, roundName: string): void {
  const r = el('hudRound');
  if (r) r.textContent = hudRoundLabel(roundIndex, roundName);
}

export function setHudBank(cents: number): void {
  const b = el('hudBank');
  if (b) b.textContent = fmtCents(cents);
  el('hudBankPill')?.classList.toggle('neg', cents < 0);
}

export function setHudStreak(streak: number | null | undefined, authed: boolean): void {
  const label = hudStreakLabel(streak, authed);
  const s = el('hudStreak');
  if (!s) return;
  const n = el('hudStreakN');
  if (n) n.textContent = label ?? '';
  s.classList.toggle('hidden', label == null);
  document.querySelector('.hud-streak-sep')?.classList.toggle('hidden', label == null);
}

/* ------------------------------- per-screen sync ------------------------------- */

let avatarHome: { parent: ParentNode; next: Node | null } | null = null;

/** Called on every screen transition (via accountMenu.syncAccountMenu):
    - hides the top bankbar on play (the HUD's 💰 cell shows the bank);
    - moves #accountMenu into the HUD's ⋯ slot on play, and back afterwards. */
export function syncPlayHud(screen: string): void {
  el('bankbar')?.classList.toggle('hud-off', screen === 'play');
  const acct = el('accountMenu');
  const slot = el('hudMenuSlot');
  if (!acct || !slot) return;
  const inSlot = acct.parentElement === slot;
  if (screen === 'play') {
    if (!inSlot) {
      if (!avatarHome && acct.parentNode) avatarHome = { parent: acct.parentNode, next: acct.nextSibling };
      slot.appendChild(acct);
    }
    acct.classList.add('in-hud');
  } else if (inSlot) {
    acct.classList.remove('in-hud');
    if (avatarHome) avatarHome.parent.insertBefore(acct, avatarHome.next && avatarHome.next.parentNode === avatarHome.parent ? avatarHome.next : null);
  }
}
